import React from 'react';
import {
  Box,
  Typography,
  Divider,
  CircularProgress,
  Grid,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Visibility as ViewIcon,
  Circle as DotIcon,
} from '@mui/icons-material';
import { EasyCheckDialog } from '../../common/Controls';
import { useCollectionDetail } from '../../../hooks/useChequeCollections';
import { formatCurrencyAmount } from '../../../utils/currencyUtils';

const DetailRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(0.75, 0),
}));

const DetailLabel = styled(Typography)({
  color: '#6B7280',
  fontSize: '0.85rem',
});

const DetailValue = styled(Typography)({
  fontWeight: 500,
  fontSize: '0.85rem',
  textAlign: 'right',
});

const TimelineItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  paddingBottom: theme.spacing(2),
  position: 'relative',
  '&:not(:last-child)::before': {
    content: '""',
    position: 'absolute',
    left: 6,
    top: 20,
    bottom: 0,
    width: 2,
    backgroundColor: theme.palette.divider,
  },
}));

const EVENT_COLORS = {
  Created: '#F59E0B',
  Dispatched: '#3B82F6',
  Delivered: '#10B981',
  Collected: '#10B981',
  Returned: '#EF4444',
};

const CollectionDetailDialog = ({ open, cheque, onClose }) => {
  const collectionId = cheque?.Id || cheque?.id || 0;
  const { data: detail, isLoading } = useCollectionDetail(collectionId > 0 ? collectionId : null);

  const chequeNumber = cheque?.ChequeNumber || cheque?.chequeNumber || '';
  const beneficiary = cheque?.BeneficiaryName || cheque?.beneficiaryName || '';
  const amount = cheque?.ChqAmount ?? cheque?.chqAmount ?? 0;
  const currency = cheque?.Currency || cheque?.currency || 'AED';
  const bankName = cheque?.BankName || cheque?.bankName || '';
  const chqDate = cheque?.ChqDate || cheque?.chqDate;
  const status = cheque?.CollectionStatus || cheque?.collectionStatus || 'Awaiting';

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';
  const formatDateTime = (d) => d ? new Date(d).toLocaleString() : '-';
  const formatCurrency = (amt) => `${currency} ${formatCurrencyAmount(amt, currency, { withSymbol: false })}`;

  return (
    <EasyCheckDialog
      open={open}
      onClose={onClose}
      title="Collection Details"
      variant="large"
      titleIcon={<ViewIcon />}
    >
      {/* Cheque Information */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Cheque Information
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <DetailRow><DetailLabel>Cheque No.</DetailLabel><DetailValue>{chequeNumber}</DetailValue></DetailRow>
          </Grid>
          <Grid item xs={6}>
            <DetailRow><DetailLabel>Beneficiary</DetailLabel><DetailValue>{beneficiary}</DetailValue></DetailRow>
          </Grid>
          <Grid item xs={6}>
            <DetailRow><DetailLabel>Amount</DetailLabel><DetailValue>{formatCurrency(amount)}</DetailValue></DetailRow>
          </Grid>
          <Grid item xs={6}>
            <DetailRow><DetailLabel>Bank</DetailLabel><DetailValue>{bankName}</DetailValue></DetailRow>
          </Grid>
          <Grid item xs={6}>
            <DetailRow><DetailLabel>Cheque Date</DetailLabel><DetailValue>{formatDate(chqDate)}</DetailValue></DetailRow>
          </Grid>
          <Grid item xs={6}>
            <DetailRow><DetailLabel>Status</DetailLabel><DetailValue>{status}</DetailValue></DetailRow>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Collection Details */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : detail ? (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Collection Details
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={1}>
              {detail.DeliveryMethod && (
                <Grid item xs={6}>
                  <DetailRow><DetailLabel>Delivery Method</DetailLabel><DetailValue>{detail.DeliveryMethod || detail.deliveryMethod}</DetailValue></DetailRow>
                </Grid>
              )}
              {(detail.CollectorName || detail.collectorName) && (
                <Grid item xs={6}>
                  <DetailRow><DetailLabel>Collector</DetailLabel><DetailValue>{detail.CollectorName || detail.collectorName}</DetailValue></DetailRow>
                </Grid>
              )}
              {(detail.CollectorId || detail.collectorId) && (
                <Grid item xs={6}>
                  <DetailRow><DetailLabel>Collector ID</DetailLabel><DetailValue>{detail.CollectorId || detail.collectorId}</DetailValue></DetailRow>
                </Grid>
              )}
              {(detail.CourierCompany || detail.courierCompany) && (
                <Grid item xs={6}>
                  <DetailRow><DetailLabel>Courier</DetailLabel><DetailValue>{detail.CourierCompany || detail.courierCompany}</DetailValue></DetailRow>
                </Grid>
              )}
              {(detail.DriverName || detail.driverName) && (
                <Grid item xs={6}>
                  <DetailRow><DetailLabel>Driver</DetailLabel><DetailValue>{detail.DriverName || detail.driverName}</DetailValue></DetailRow>
                </Grid>
              )}
              {(detail.TrackingReference || detail.trackingReference) && (
                <Grid item xs={6}>
                  <DetailRow><DetailLabel>Tracking Ref</DetailLabel><DetailValue>{detail.TrackingReference || detail.trackingReference}</DetailValue></DetailRow>
                </Grid>
              )}
              {(detail.ReceivedBy || detail.receivedBy) && (
                <Grid item xs={6}>
                  <DetailRow><DetailLabel>Received By</DetailLabel><DetailValue>{detail.ReceivedBy || detail.receivedBy}</DetailValue></DetailRow>
                </Grid>
              )}
              {(detail.CollectionDate || detail.collectionDate) && (
                <Grid item xs={6}>
                  <DetailRow><DetailLabel>Collection Date</DetailLabel><DetailValue>{formatDateTime(detail.CollectionDate || detail.collectionDate)}</DetailValue></DetailRow>
                </Grid>
              )}
              {(detail.DeliveredDate || detail.deliveredDate) && (
                <Grid item xs={6}>
                  <DetailRow><DetailLabel>Delivered Date</DetailLabel><DetailValue>{formatDateTime(detail.DeliveredDate || detail.deliveredDate)}</DetailValue></DetailRow>
                </Grid>
              )}
              {(detail.ReturnReason || detail.returnReason) && (
                <Grid item xs={6}>
                  <DetailRow><DetailLabel>Return Reason</DetailLabel><DetailValue>{detail.ReturnReason || detail.returnReason}</DetailValue></DetailRow>
                </Grid>
              )}
              {(detail.Remarks || detail.remarks) && (
                <Grid item xs={12}>
                  <DetailRow><DetailLabel>Remarks</DetailLabel><DetailValue>{detail.Remarks || detail.remarks}</DetailValue></DetailRow>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Timeline */}
          {(detail.Events || detail.events)?.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Collection Timeline
              </Typography>
              <Box>
                {(detail.Events || detail.events).map((event, idx) => (
                  <TimelineItem key={event.Id || event.id || idx}>
                    <DotIcon sx={{ fontSize: 14, mt: 0.5, color: EVENT_COLORS[event.EventType || event.eventType] || '#6B7280' }} />
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={event.EventType || event.eventType}
                          size="small"
                          sx={{
                            fontSize: '0.7rem',
                            height: 22,
                            backgroundColor: EVENT_COLORS[event.EventType || event.eventType] + '20',
                            color: EVENT_COLORS[event.EventType || event.eventType],
                            fontWeight: 600,
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(event.EventDate || event.eventDate)}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        by {event.PerformedBy || event.performedBy}
                      </Typography>
                      {(event.Notes || event.notes) && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {event.Notes || event.notes}
                        </Typography>
                      )}
                    </Box>
                  </TimelineItem>
                ))}
              </Box>
            </>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No collection record found. This cheque is awaiting collection.
        </Typography>
      )}
    </EasyCheckDialog>
  );
};

export default CollectionDetailDialog;
